/**
 * Accordion custom element.
 *
 * Enhances native <details>/<summary> with:
 * - Exclusive mode (only one panel open at a time)
 * - Keyboard navigation (ArrowUp/Down, Home, End)
 * - data-state attribute for styling
 *
 * Without JS, <details>/<summary> works natively — all panels are
 * independently toggleable. This is progressive enhancement only.
 */
class ThemeAccordion extends HTMLElement {
  connectedCallback() {
    this.exclusive = this.dataset.exclusive !== 'false';
    this.addEventListener('click', this);
    this.addEventListener('keydown', this);
    this.#syncState();
  }

  handleEvent(e) {
    if (e.type === 'click') this.#handleClick(e);
    if (e.type === 'keydown') this.#handleKeydown(e);
  }

  get #items() {
    return [...this.querySelectorAll(':scope > .shopify-block > details[data-slot="accordion-item"], :scope > details[data-slot="accordion-item"]')];
  }

  get #headers() {
    return this.#items.map(item => item.querySelector('summary'));
  }

  #handleClick(e) {
    const summary = e.target.closest('summary[data-slot="accordion-header"]');
    if (!summary || summary.getAttribute('aria-disabled') === 'true') return;

    const item = summary.closest('details');
    if (!item || !this.exclusive) {
      this.#syncState();
      return;
    }

    // Exclusive: close all other panels
    e.preventDefault();
    const wasOpen = item.open;

    for (const other of this.#items) {
      if (other !== item) other.open = false;
    }
    item.open = !wasOpen;
    this.#syncState();
  }

  #handleKeydown(e) {
    const summary = e.target.closest('summary[data-slot="accordion-header"]');
    if (!summary) return;

    const headers = this.#headers;
    const current = headers.indexOf(summary);
    if (current === -1) return;

    let next = -1;
    switch (e.key) {
      case 'ArrowDown': next = (current + 1) % headers.length; break;
      case 'ArrowUp': next = (current - 1 + headers.length) % headers.length; break;
      case 'Home': next = 0; break;
      case 'End': next = headers.length - 1; break;
      default: return;
    }

    e.preventDefault();
    headers[next]?.focus();
  }

  #syncState() {
    for (const item of this.#items) {
      item.setAttribute('data-state', item.open ? 'open' : 'closed');
      const summary = item.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', String(item.open));
    }
  }
}

if (!customElements.get('theme-accordion')) {
  customElements.define('theme-accordion', ThemeAccordion);
}
